import { expressURL, getAuthHeaders } from '../utils/networkUtils';
import {
  assertEncryptedManifest,
  assertEncryptedShareEnvelope,
  assertPublicRecoveryJwk,
  assertRecoveryVaultEnvelope,
  assertRequestBoundEnvelope,
} from './payloadSafety.ts';
import type {
  ActivateCircleInput,
  CircleStatus,
  CreateCircleInput,
  CreateRecoveryRequestInput,
  DecideRecoveryRequestInput,
  MembershipStatus,
  RecoveryAssignment,
  RecoveryCircle,
  RecoveryCircleMember,
  RecoveryDecision,
  RecoveryDevice,
  RecoveryInstitution,
  RecoveryInvitation,
  RecoveryOverview,
  RecoveryRequest,
  RecoveryRequestPermissions,
  RecoveryRequestStatus,
  RecoveryUser,
  RegisterRecoveryDeviceInput,
  RespondToMembershipInput,
} from './types.ts';

type RecoveryFetchOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
};

export class RecoveryApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'RecoveryApiError';
    this.status = status;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseJson(text: string): unknown {
  if (!text.trim()) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

function errorMessage(body: unknown, response: Response): string {
  if (isRecord(body) && typeof body.error === 'string' && body.error.trim()) {
    return body.error.trim();
  }
  if (response.statusText) {
    return `${response.statusText} (HTTP ${response.status})`;
  }
  return `Recovery service request failed (HTTP ${response.status})`;
}

function apiUrl(path: string): string {
  const base = expressURL().replace(/\/$/, '');
  return `${base}${path}`;
}

export async function authenticatedRecoveryFetch<T>(
  path: string,
  options: RecoveryFetchOptions = {}
): Promise<T> {
  const authConfig = getAuthHeaders() as {
    headers?: Record<string, string>;
  };
  const headers = new Headers(authConfig.headers);

  if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  headers.set('Accept', 'application/json');

  const response = await fetch(apiUrl(path), {
    ...options,
    credentials: 'include',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
  const text = await response.text();
  const parsed = parseJson(text);

  if (!response.ok) {
    throw new RecoveryApiError(errorMessage(parsed, response), response.status);
  }
  if (!text.trim() || response.status === 204) {
    return undefined as T;
  }
  if (parsed === undefined) {
    throw new RecoveryApiError(
      'The recovery service returned an unreadable response.',
      response.status
    );
  }
  return parsed as T;
}

function optionalString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number') return String(value);
  return undefined;
}

function requiredString(value: unknown, label: string): string {
  const result = optionalString(value);
  if (!result) {
    throw new RecoveryApiError(
      `The recovery response is missing ${label}.`,
      200
    );
  }
  return result;
}

function optionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function optionalDate(value: unknown): string | null | undefined {
  if (value === null) return null;
  return typeof value === 'string' ? value : undefined;
}

function normalizeMembership(
  value: unknown,
  index: number
): RecoveryCircleMember {
  if (!isRecord(value)) {
    throw new RecoveryApiError('A recovery membership was invalid.', 200);
  }
  const id = requiredString(value.id ?? value.membershipId, 'a membership id');
  const holder = isRecord(value.holder) ? value.holder : null;
  const holderType = value.holderType === 'institution' ? 'institution' : 'user';
  const allowedStatuses = new Set<MembershipStatus>([
    'invited',
    'accepted',
    'declined',
    'revoked',
  ]);
  const status = allowedStatuses.has(value.status as MembershipStatus)
    ? (value.status as MembershipStatus)
    : 'invited';
  return {
    id,
    membershipId: optionalString(value.membershipId) || id,
    position: optionalNumber(value.position) || index + 1,
    holderType,
    holderUserId:
      typeof value.holderUserId === 'string' ||
      typeof value.holderUserId === 'number'
        ? value.holderUserId
        : undefined,
    displayName:
      optionalString(value.displayName) ||
      optionalString(holder?.fullName) ||
      optionalString(holder?.shortName) ||
      optionalString(value.holderInstitutionId) ||
      null,
    identityHidden: value.identityHidden === true,
    isViewer: value.isViewer === true,
    custodyPublicKeyJwk: isRecord(value.custodyPublicKeyJwk)
      ? (value.custodyPublicKeyJwk as RecoveryCircleMember['custodyPublicKeyJwk'])
      : null,
    custodyPublicKeyFingerprint: optionalString(value.custodyPublicKeyFingerprint) || null,
    status,
    invitedAt: optionalDate(value.invitedAt),
    respondedAt: optionalDate(value.respondedAt),
  };
}

function normalizeCircleStatus(value: unknown): CircleStatus {
  const aliases: Record<string, CircleStatus> = {
    draft: 'draft',
    inviting: 'inviting',
    ready: 'ready',
    activating: 'ready',
    active: 'active',
    rotation_required: 'rotation_required',
    superseded: 'disabled',
    disabled: 'disabled',
  };
  const status = typeof value === 'string' ? aliases[value] : undefined;
  if (!status) {
    throw new RecoveryApiError('A recovery circle had an unknown state.', 200);
  }
  return status;
}

function normalizeCircle(value: unknown): RecoveryCircle {
  if (!isRecord(value) || !Array.isArray(value.members)) {
    throw new RecoveryApiError('A recovery circle response was invalid.', 200);
  }
  const members = value.members.map(normalizeMembership);
  const threshold = optionalNumber(value.threshold);
  if (!threshold) {
    throw new RecoveryApiError('A recovery circle is missing its threshold.', 200);
  }
  return {
    id: requiredString(value.id, 'a circle id'),
    ownerUserId:
      typeof value.ownerUserId === 'string' ||
      typeof value.ownerUserId === 'number'
        ? value.ownerUserId
        : undefined,
    version: optionalNumber(value.version),
    status: normalizeCircleStatus(value.status ?? value.state),
    threshold,
    memberCount: optionalNumber(value.memberCount) || members.length,
    revealMemberNames: value.revealMemberNames === true,
    members,
    rotationRequired: value.rotationRequired === true,
    createdAt: optionalDate(value.createdAt),
    activatedAt: optionalDate(value.activatedAt),
    updatedAt: optionalDate(value.updatedAt),
    activationManifest: isRecord(value.activationManifest)
      ? assertEncryptedManifest(
          value.activationManifest as RecoveryCircle['activationManifest'] & object
        )
      : null,
    vaultEnvelope: isRecord(value.vaultEnvelope)
      ? assertRecoveryVaultEnvelope(value.vaultEnvelope)
      : null,
  };
}

interface NormalizedDisclosure {
  circle: RecoveryCircle | null;
  circleId: string;
  ownerLabel: string;
  memberCount: number;
  threshold: number;
  revealMemberNames: boolean;
  institutionPresent: boolean;
  memberNames?: string[];
}

function normalizeDisclosure(value: Record<string, unknown>): NormalizedDisclosure {
  const circle = isRecord(value.circle) ? normalizeCircle(value.circle) : null;
  const namesFromResponse = Array.isArray(value.memberNames)
    ? value.memberNames.filter(
        (name): name is string => typeof name === 'string' && Boolean(name.trim())
      )
    : undefined;
  const visibleCircleNames = circle?.members
    .filter((member) => !member.identityHidden && Boolean(member.displayName))
    .map((member) => member.displayName as string);
  const memberNames =
    namesFromResponse?.length
      ? namesFromResponse
      : visibleCircleNames?.length
        ? visibleCircleNames
        : undefined;
  return {
    circle,
    circleId: requiredString(value.circleId ?? circle?.id, 'a circle id'),
    ownerLabel:
      optionalString(value.ownerLabel) ||
      optionalString(
        isRecord(value.owner) ? value.owner.fullName : undefined
      ) ||
      'Another adult',
    memberCount:
      optionalNumber(value.memberCount) || circle?.memberCount || 0,
    threshold: optionalNumber(value.threshold) || circle?.threshold || 0,
    revealMemberNames:
      typeof value.revealMemberNames === 'boolean'
        ? value.revealMemberNames
        : circle?.revealMemberNames === true,
    institutionPresent:
      typeof value.institutionPresent === 'boolean'
        ? value.institutionPresent
        : Boolean(
            circle?.members.some((member) => member.holderType === 'institution')
          ),
    memberNames,
  };
}

function normalizeInvitation(value: unknown): RecoveryInvitation {
  if (!isRecord(value)) {
    throw new RecoveryApiError('A recovery invitation was invalid.', 200);
  }
  const disclosure = normalizeDisclosure(value);
  return {
    id: requiredString(value.id, 'an invitation id'),
    status: 'invited',
    circleId: disclosure.circleId,
    ownerLabel: disclosure.ownerLabel,
    memberCount: disclosure.memberCount,
    threshold: disclosure.threshold,
    revealMemberNames: disclosure.revealMemberNames,
    institutionPresent: disclosure.institutionPresent,
    memberNames: disclosure.memberNames,
    invitedAt: optionalDate(value.invitedAt),
    expiresAt: optionalDate(value.expiresAt),
  };
}

function normalizeAssignment(value: unknown): RecoveryAssignment {
  if (!isRecord(value)) {
    throw new RecoveryApiError('A recovery assignment was invalid.', 200);
  }
  const disclosure = normalizeDisclosure(value);
  const status: RecoveryAssignment['status'] =
    value.status === 'declined' || value.status === 'revoked'
      ? value.status
      : 'accepted';
  return {
    id: requiredString(value.id, 'an assignment id'),
    status,
    circleId: disclosure.circleId,
    ownerLabel: disclosure.ownerLabel,
    memberCount: disclosure.memberCount,
    threshold: disclosure.threshold,
    revealMemberNames: disclosure.revealMemberNames,
    institutionPresent: disclosure.institutionPresent,
    memberNames: disclosure.memberNames,
    acceptedAt: optionalDate(value.acceptedAt ?? value.respondedAt),
    updatedAt: optionalDate(value.updatedAt),
    rotationRequired:
      value.rotationRequired === true ||
      disclosure.circle?.rotationRequired === true,
    encryptedShare: isRecord(value.encryptedShare)
      ? assertEncryptedShareEnvelope(
          value.encryptedShare as NonNullable<RecoveryAssignment['encryptedShare']>
        )
      : null,
    activationManifest: isRecord(value.activationManifest)
      ? assertEncryptedManifest(
          value.activationManifest as RecoveryAssignment['activationManifest'] & object
        )
      : disclosure.circle?.activationManifest || null,
  };
}

function normalizeDevice(value: unknown): RecoveryDevice {
  if (!isRecord(value)) {
    throw new RecoveryApiError('A recovery device was invalid.', 200);
  }
  return {
    id: requiredString(value.id, 'a device id'),
    name: requiredString(value.name, 'a device name'),
    fingerprint: optionalString(value.fingerprint) || null,
    custodyPublicKeyJwk: isRecord(value.custodyPublicKeyJwk)
      ? (value.custodyPublicKeyJwk as JsonWebKey)
      : null,
    stewardEligible: value.stewardEligible === true,
    status: value.status === 'revoked' ? 'revoked' : 'active',
    registeredAt: optionalDate(value.registeredAt ?? value.createdAt),
    lastSeenAt: optionalDate(value.lastSeenAt),
  };
}

function normalizeInstitution(value: unknown): RecoveryInstitution {
  if (!isRecord(value)) {
    throw new RecoveryApiError('A recovery institution entry was invalid.', 200);
  }
  return {
    id: requiredString(value.id, 'an institution id'),
    name: requiredString(value.name, 'an institution name'),
    integrationStatus: 'unavailable',
    description: optionalString(value.description),
  };
}

function requestStatus(
  value: unknown,
  availableAt: string,
  now = Date.now()
): RecoveryRequestStatus {
  const direct = new Set<RecoveryRequestStatus>([
    'pending_delay',
    'awaiting_approvals',
    'ready',
    'completed',
    'cancelled',
    'denied',
    'reported',
    'expired',
    'invalid',
  ]);
  if (direct.has(value as RecoveryRequestStatus)) {
    return value as RecoveryRequestStatus;
  }
  if (value === 'awaiting-approvals') {
    const delay = new Date(availableAt).getTime();
    return Number.isFinite(delay) && now < delay
      ? 'pending_delay'
      : 'awaiting_approvals';
  }
  if (value === 'quorum-met') return 'pending_delay';
  if (value === 'releasable') return 'ready';
  if (value === 'completed' || value === 'cancelled' || value === 'reported' || value === 'expired') {
    return value;
  }
  return 'invalid';
}

function explicitPermissions(value: unknown): RecoveryRequestPermissions | null {
  if (!isRecord(value)) return null;
  const keys = ['approve', 'deny', 'report', 'cancel', 'complete'] as const;
  if (!keys.every((key) => typeof value[key] === 'boolean')) return null;
  return {
    approve: value.approve as boolean,
    deny: value.deny as boolean,
    report: value.report as boolean,
    cancel: value.cancel as boolean,
    complete: value.complete as boolean,
  };
}

interface RequestNormalizationContext {
  ownedCircle: RecoveryCircle | null;
  assignments: RecoveryAssignment[];
  devices: RecoveryDevice[];
  viewerRole?: 'owner' | 'keyholder';
  detailAssignment?: Record<string, unknown> | null;
}

function normalizeRequest(
  value: unknown,
  context: RequestNormalizationContext
): RecoveryRequest {
  if (!isRecord(value)) {
    throw new RecoveryApiError('A recovery request response was invalid.', 200);
  }
  const id = requiredString(value.id, 'a request id');
  const circleId = requiredString(value.circleId, 'a request circle id');
  const createdAt = requiredString(value.createdAt, 'a request creation time');
  const availableAt = requiredString(
    value.availableAt ?? value.delayUntil,
    'a request availability time'
  );
  const expiresAt = requiredString(value.expiresAt, 'a request expiry time');
  const relatedAssignment = context.assignments.find(
    (assignment) => assignment.circleId === circleId
  );
  const viewerRole =
    value.viewerRole === 'owner' || value.viewerRole === 'keyholder'
      ? value.viewerRole
      : context.viewerRole ||
        (context.ownedCircle?.id === circleId ? 'owner' : 'keyholder');
  const decisions = Array.isArray(value.decisions) ? value.decisions : [];
  const keyholderMembershipId =
    optionalString(context.detailAssignment?.id) || relatedAssignment?.id;
  const ownDecision = decisions.find(
    (decision) =>
      isRecord(decision) &&
      keyholderMembershipId !== undefined &&
      optionalString(decision.membershipId) === keyholderMembershipId
  );
  const decisionValue =
    isRecord(ownDecision) &&
    ['approve', 'deny', 'report'].includes(String(ownDecision.decision))
      ? (ownDecision.decision as RecoveryDecision)
      : undefined;
  const quorum = isRecord(value.quorum) ? value.quorum : null;
  const rawState = value.state ?? value.status;
  const status = requestStatus(rawState, availableAt);
  const openServerStates = new Set([
    'awaiting-approvals',
    'quorum-met',
    'releasable',
  ]);
  const explicit = explicitPermissions(value.permissions);
  const mayDecide =
    viewerRole === 'keyholder' &&
    !decisionValue &&
    (openServerStates.has(String(rawState)) ||
      ['pending_delay', 'awaiting_approvals', 'ready'].includes(status));
  const permissions: RecoveryRequestPermissions =
    explicit || {
      approve: mayDecide,
      deny: mayDecide,
      report: mayDecide,
      cancel:
        viewerRole === 'owner' &&
        (openServerStates.has(String(rawState)) ||
          rawState === 'reported' ||
          ['pending_delay', 'awaiting_approvals', 'ready'].includes(status)),
      complete:
        viewerRole === 'owner' &&
        (rawState === 'releasable' || status === 'ready'),
    };
  const targetDeviceId = requiredString(value.targetDeviceId, 'a target device id');
  const target = context.devices.find((device) => device.id === targetDeviceId);
  const detailEncryptedShare = isRecord(context.detailAssignment?.encryptedShare)
    ? context.detailAssignment?.encryptedShare
    : undefined;
  const releases: NonNullable<RecoveryRequest['releases']> = [];
  if (Array.isArray(value.releases)) {
    for (const release of value.releases) {
      if (
        !isRecord(release) ||
        !optionalString(release.membershipId) ||
        !isRecord(release.sealedShare)
      ) {
        throw new RecoveryApiError(
          'A released encrypted share response was invalid.',
          200
        );
      }
      const sealedShare = assertRequestBoundEnvelope(
        release.sealedShare as NonNullable<
          RecoveryRequest['releases']
        >[number]['sealedShare'],
        id
      );
      releases.push({
        membershipId: requiredString(
          release.membershipId,
          'a released membership id'
        ),
        sealedShare,
      });
    }
  }

  return {
    id,
    circleId,
    circleVersion: optionalNumber(value.circleVersion),
    type: 'lockout',
    mode: value.mode === 'authenticated-drill' ? value.mode : undefined,
    status,
    viewerRole,
    ownerLabel: optionalString(value.ownerLabel) || relatedAssignment?.ownerLabel,
    targetDeviceId,
    targetLabel:
      optionalString(value.targetLabel) ||
      optionalString(isRecord(value.target) ? value.target.label : undefined) ||
      target?.name ||
      'registered target device',
    targetFingerprint:
      optionalString(value.targetFingerprint) ||
      optionalString(
        isRecord(value.target) ? value.target.fingerprint : undefined
      ) ||
      target?.fingerprint ||
      null,
    targetPublicKeyJwk: isRecord(value.targetPublicKeyJwk)
      ? (value.targetPublicKeyJwk as JsonWebKey)
      : null,
    createdAt,
    availableAt,
    expiresAt,
    completedAt: optionalDate(value.completedAt),
    approvalCount:
      optionalNumber(value.approvalCount) ||
      optionalNumber(quorum?.approvals) ||
      0,
    approvalsRequired:
      optionalNumber(value.approvalsRequired) ||
      optionalNumber(quorum?.threshold) ||
      optionalNumber(value.threshold) ||
      0,
    institutionRequired:
      value.institutionRequired === true || quorum?.institutionRequired === true,
    myDecision:
      value.myDecision === 'approve' ||
      value.myDecision === 'deny' ||
      value.myDecision === 'report'
        ? value.myDecision
        : decisionValue || null,
    permissions,
    keyholderMembershipId,
    keyholderEncryptedShare: isRecord(detailEncryptedShare)
      ? assertEncryptedShareEnvelope(
          detailEncryptedShare as NonNullable<RecoveryAssignment['encryptedShare']>
        )
      : relatedAssignment?.encryptedShare || null,
    activationManifest: isRecord(value.activationManifest)
      ? assertEncryptedManifest(
          value.activationManifest as RecoveryRequest['activationManifest'] & object
        )
      : isRecord(context.detailAssignment?.activationManifest)
        ? assertEncryptedManifest(
            context.detailAssignment?.activationManifest as RecoveryRequest['activationManifest'] &
              object
          )
        : relatedAssignment?.activationManifest ||
          context.ownedCircle?.activationManifest ||
          null,
    vaultEnvelope: isRecord(value.vaultEnvelope)
      ? assertRecoveryVaultEnvelope(value.vaultEnvelope)
      : context.ownedCircle?.vaultEnvelope || null,
    releases: releases.length ? releases : undefined,
  };
}

export function parseRecoveryOverview(value: unknown): RecoveryOverview {
  if (!isRecord(value)) {
    throw new RecoveryApiError('The recovery overview response was not an object.', 200);
  }
  if (!Object.prototype.hasOwnProperty.call(value, 'ownedCircle')) {
    throw new RecoveryApiError(
      'The recovery overview response is missing "ownedCircle".',
      200
    );
  }
  const invitationValues = value.invitations;
  const assignmentValues = value.assignments ?? value.acceptedAssignments;
  const requestValues = value.requests;
  const deviceValues = value.devices;
  const institutionValues = value.institutions ?? value.institutionDirectory;
  const lists = [
    ['invitations', invitationValues],
    ['assignments', assignmentValues],
    ['requests', requestValues],
    ['devices', deviceValues],
    ['institutions', institutionValues],
  ] as const;
  for (const [name, list] of lists) {
    if (!Array.isArray(list)) {
      throw new RecoveryApiError(
        `The recovery overview response is missing "${name}".`,
        200
      );
    }
  }

  const invitationList = invitationValues as unknown[];
  const assignmentList = assignmentValues as unknown[];
  const requestList = requestValues as unknown[];
  const deviceList = deviceValues as unknown[];
  const institutionList = institutionValues as unknown[];
  const ownedCircle =
    value.ownedCircle === null ? null : normalizeCircle(value.ownedCircle);
  const invitations = invitationList.map(normalizeInvitation);
  const assignments = assignmentList.map(normalizeAssignment);
  const devices = deviceList.map(normalizeDevice);
  const institutions = institutionList.map(normalizeInstitution);
  const requests = requestList.map((request) =>
    normalizeRequest(request, { ownedCircle, assignments, devices })
  );
  return {
    ownedCircle,
    invitations,
    assignments,
    requests,
    devices,
    institutions,
  };
}

function parseRecoveryRequest(value: unknown): RecoveryRequest {
  if (!isRecord(value)) {
    throw new RecoveryApiError('The recovery request response was invalid.', 200);
  }
  const requestValue = isRecord(value.request) ? value.request : value;
  const detailAssignment = isRecord(value.assignment) ? value.assignment : null;
  return normalizeRequest(requestValue, {
    ownedCircle: null,
    assignments: [],
    devices: [],
    viewerRole: detailAssignment ? 'keyholder' : 'owner',
    detailAssignment,
  });
}

function parseRecoveryUsers(value: unknown): RecoveryUser[] {
  if (!Array.isArray(value)) {
    throw new RecoveryApiError('The contacts response was invalid.', 200);
  }
  return value.filter(
    (user): user is RecoveryUser =>
      isRecord(user) &&
      (typeof user.id === 'string' || typeof user.id === 'number') &&
      typeof user.fullName === 'string'
  );
}

function mutation(
  path: string,
  method: 'POST' | 'DELETE',
  body?: unknown
): Promise<void> {
  return authenticatedRecoveryFetch<void>(path, { method, body });
}

export interface RecoveryApiClient {
  getOverview(signal?: AbortSignal): Promise<RecoveryOverview>;
  getUsers(signal?: AbortSignal): Promise<RecoveryUser[]>;
  registerDevice(input: RegisterRecoveryDeviceInput): Promise<void>;
  revokeDevice(deviceId: string): Promise<void>;
  createCircle(input: CreateCircleInput): Promise<void>;
  respondToMembership(
    membershipId: string,
    input: RespondToMembershipInput
  ): Promise<void>;
  activateCircle(circleId: string, input: ActivateCircleInput): Promise<void>;
  rotateCircle(circleId: string, input: ActivateCircleInput): Promise<void>;
  retireCircle(circleId: string): Promise<void>;
  createRequest(input: CreateRecoveryRequestInput): Promise<void>;
  getRequest(requestId: string, signal?: AbortSignal): Promise<RecoveryRequest>;
  decideRequest(
    requestId: string,
    input: DecideRecoveryRequestInput
  ): Promise<void>;
  cancelRequest(requestId: string): Promise<void>;
  completeRequest(requestId: string): Promise<void>;
}

export const recoveryApi: RecoveryApiClient = {
  async getOverview(signal) {
    const value = await authenticatedRecoveryFetch<unknown>(
      '/api/recovery/overview',
      { signal }
    );
    return parseRecoveryOverview(value);
  },

  async getUsers(signal) {
    const value = await authenticatedRecoveryFetch<unknown>('/api/get-users', {
      signal,
    });
    return parseRecoveryUsers(value);
  },

  registerDevice(input) {
    assertPublicRecoveryJwk(input.custodyPublicKeyJwk, 'The device custody key');
    return mutation('/api/recovery/devices', 'POST', input);
  },

  revokeDevice(deviceId) {
    return mutation(
      `/api/recovery/devices/${encodeURIComponent(deviceId)}`,
      'DELETE'
    );
  },

  createCircle(input) {
    return mutation('/api/recovery/circles', 'POST', input);
  },

  respondToMembership(membershipId, input) {
    if (input.decision === 'accept') {
      assertPublicRecoveryJwk(
        input.custodyPublicKeyJwk,
        'The membership custody key'
      );
    }
    return mutation(
      `/api/recovery/memberships/${encodeURIComponent(membershipId)}/respond`,
      'POST',
      input
    );
  },

  activateCircle(circleId, input) {
    const manifest = assertEncryptedManifest(input.manifest);
    assertRecoveryVaultEnvelope(input.vaultEnvelope);
    if (manifest.circleId !== circleId) {
      throw new Error('The activation manifest is bound to a different circle.');
    }
    input.encryptedShares.forEach((share) => {
      const envelope = assertEncryptedShareEnvelope(share.envelope);
      if (
        envelope.context.purpose !== 'initial-share-custody' ||
        envelope.context.circleId !== circleId ||
        envelope.context.membershipId !== share.membershipId ||
        envelope.context.circleVersion !== manifest.circleVersion
      ) {
        throw new Error(
          'An encrypted membership envelope is not bound to this activation.'
        );
      }
    });
    return mutation(
      `/api/recovery/circles/${encodeURIComponent(circleId)}/activate`,
      'POST',
      input
    );
  },

  rotateCircle(circleId, input) {
    const manifest = assertEncryptedManifest(input.manifest);
    assertRecoveryVaultEnvelope(input.vaultEnvelope);
    if (manifest.circleId !== circleId) {
      throw new Error('The rotation manifest is bound to a different circle.');
    }
    input.encryptedShares.forEach((share) => {
      const envelope = assertEncryptedShareEnvelope(share.envelope);
      if (
        envelope.context.purpose !== 'initial-share-custody' ||
        envelope.context.circleId !== circleId ||
        envelope.context.membershipId !== share.membershipId ||
        envelope.context.circleVersion !== manifest.circleVersion
      ) {
        throw new Error(
          'An encrypted membership envelope is not bound to this rotation.'
        );
      }
    });
    return mutation(
      `/api/recovery/circles/${encodeURIComponent(circleId)}/rotate`,
      'POST',
      input
    );
  },

  retireCircle(circleId) {
    return mutation(
      `/api/recovery/circles/${encodeURIComponent(circleId)}/retire`,
      'POST'
    );
  },

  createRequest(input) {
    assertPublicRecoveryJwk(input.targetPublicKeyJwk, 'The recovery target key');
    return mutation('/api/recovery/requests', 'POST', input);
  },

  async getRequest(requestId, signal) {
    const value = await authenticatedRecoveryFetch<unknown>(
      `/api/recovery/requests/${encodeURIComponent(requestId)}`,
      { signal }
    );
    return parseRecoveryRequest(value);
  },

  decideRequest(requestId, input) {
    if (input.decision === 'approve') {
      assertRequestBoundEnvelope(input.sealedShare, requestId);
    }
    return mutation(
      `/api/recovery/requests/${encodeURIComponent(requestId)}/decide`,
      'POST',
      input
    );
  },

  cancelRequest(requestId) {
    return mutation(
      `/api/recovery/requests/${encodeURIComponent(requestId)}/cancel`,
      'POST'
    );
  },

  completeRequest(requestId) {
    return mutation(
      `/api/recovery/requests/${encodeURIComponent(requestId)}/complete`,
      'POST'
    );
  },
};
